import React from 'react';
import { Button } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { ArrowBackRounded as BackIcon } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';

export default function BackButton({
  fallbackTo = '/',
  sx,
  hideOnPath = '/',
  tone = 'auto',
  onClick,
  disabled = false,
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const canGoBack = (() => {
    try {
      const idx = window.history?.state?.idx;
      if (typeof idx === 'number') return idx > 0;
      return window.history.length > 1;
    } catch {
      return false;
    }
  })();

  if (location?.pathname === hideOnPath) return null;

  const handleBack = () => {
    if (typeof onClick === 'function') {
      onClick();
      return;
    }
    if (canGoBack) {
      navigate(-1);
      return;
    }
    navigate(fallbackTo, { replace: true });
  };

  const primary = theme.palette.primary.main;
  const isLightTone = tone === 'light';
  const baseColor = isLightTone ? theme.palette.common.white : primary;

  return (
    <Button
      onClick={handleBack}
      disabled={disabled}
      startIcon={<BackIcon />}
      variant="outlined"
      sx={{
        borderRadius: '999px',
        textTransform: 'none',
        fontWeight: 700,
        px: 2.5,
        py: 0.75,
        minHeight: 40,
        borderWidth: 1.5,
        color: baseColor,
        borderColor: alpha(baseColor, 0.55),
        backgroundColor: isLightTone ? alpha(theme.palette.common.white, 0.10) : alpha(primary, 0.06),
        backdropFilter: 'blur(8px)',
        '&:hover': {
          borderWidth: 1.5,
          color: theme.palette.common.white,
          borderColor: alpha(theme.palette.common.white, 0.85),
          backgroundColor: 'transparent',
          backgroundImage:
            'linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)',
        },
        '& .MuiButton-startIcon': {
          marginRight: '6px',
        },
        ...sx,
      }}
    >
      Back
    </Button>
  );
}
